/**
 * Time helpers for scheduled notifications. Pure functions (unit-tested in
 * scheduleTime.test.js).
 *
 * The admin picks a wall-clock time with <input type="datetime-local">. It is
 * interpreted in the STORE's time zone (a tenant setting, passed in as
 * `timeZone`) — not the zone of whichever device the admin happens to be on —
 * and sent to the API as an ISO instant with an explicit offset
 * (toISOString → "…Z"); the server rejects offset-less times and stores UTC.
 * When no `timeZone` is given the browser's own zone is used.
 */

export const MIN_LEAD_SECONDS = 60;
export const MAX_HORIZON_DAYS = 90;

const LOCAL_RE = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/;
const pad = (n) => String(n).padStart(2, "0");

export function browserTimeZone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
}

/** Date → "YYYY-MM-DDTHH:mm" in local time (datetime-local value). */
export function toLocalInputValue(date) {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
    + `T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

/** Wall-clock fields of an instant as read in an IANA time zone. */
export function zonedParts(date, timeZone) {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone, year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", hourCycle: "h23",
  }).formatToParts(date);
  const get = (type) => Number(parts.find((p) => p.type === type).value);
  return { y: get("year"), mo: get("month"), d: get("day"), h: get("hour") % 24, mi: get("minute") };
}

/** Date → "YYYY-MM-DDTHH:mm" as read in `timeZone` (datetime-local value). */
export function toZonedInputValue(date, timeZone) {
  if (!timeZone) return toLocalInputValue(date);
  const p = zonedParts(date, timeZone);
  return `${p.y}-${pad(p.mo)}-${pad(p.d)}T${pad(p.h)}:${pad(p.mi)}`;
}

/** Today's date ("YYYY-MM-DD") in `timeZone`. */
export function todayInZone(timeZone, now = new Date()) {
  return toZonedInputValue(now, timeZone).slice(0, 10);
}

/**
 * The instant at which a clock in `timeZone` reads the given wall time.
 * A time that does not exist there (spring-forward gap) resolves to the
 * later instant, i.e. it moves forward by the gap — the same thing a
 * browser does for its own zone.
 */
export function zonedWallTimeToDate(y, mo, d, h, mi, timeZone) {
  const target = Date.UTC(y, mo - 1, d, h, mi);
  const wallAt = (t) => {
    const p = zonedParts(new Date(t), timeZone);
    return Date.UTC(p.y, p.mo - 1, p.d, p.h, p.mi);
  };
  let t = target;
  let previous = null;
  for (let i = 0; i < 4; i += 1) {
    const diff = target - wallAt(t);
    if (diff === 0) return new Date(t);
    previous = t;
    t += diff;
  }
  return new Date(Math.max(t, previous));
}

/** Earliest value the picker should offer: now + lead, rounded up to a minute. */
export function minInputValue(now = new Date(), minLeadSeconds = MIN_LEAD_SECONDS, timeZone) {
  const ms = now.getTime() + minLeadSeconds * 1000;
  return toZonedInputValue(new Date(Math.ceil(ms / 60000) * 60000), timeZone);
}

export function maxInputValue(now = new Date(), maxHorizonDays = MAX_HORIZON_DAYS, timeZone) {
  return toZonedInputValue(new Date(now.getTime() + maxHorizonDays * 86400000), timeZone);
}

/**
 * Validate a datetime-local value.
 * Returns { ok: true, date, iso, notice } or { ok: false, error }.
 *
 * `notice` is set when the wall-clock time does not exist in the admin's
 * zone (spring-forward gap): the browser moves it forward, and the admin is
 * told the time that will actually be used.
 */
export function validateSchedule(value, {
  now = new Date(),
  minLeadSeconds = MIN_LEAD_SECONDS,
  maxHorizonDays = MAX_HORIZON_DAYS,
  timeZone,
} = {}) {
  if (!value) return { ok: false, error: "Pick a date and time." };
  const m = LOCAL_RE.exec(value);
  if (!m) return { ok: false, error: "Enter a valid date and time." };
  const [y, mo, d, h, mi] = m.slice(1).map(Number);
  // JS rolls impossible dates over (31 Apr → 1 May); refuse instead.
  const probe = new Date(Date.UTC(y, mo - 1, d));
  if (Number.isNaN(probe.getTime()) || probe.getUTCMonth() !== mo - 1 || probe.getUTCDate() !== d
      || h > 23 || mi > 59) {
    return { ok: false, error: "That date doesn't exist." };
  }
  const date = timeZone
    ? zonedWallTimeToDate(y, mo, d, h, mi, timeZone)
    : new Date(y, mo - 1, d, h, mi, 0, 0);
  if (Number.isNaN(date.getTime())) return { ok: false, error: "Enter a valid date and time." };
  if (date.getTime() <= now.getTime()) {
    return { ok: false, error: "That time has already passed." };
  }
  if (date.getTime() < now.getTime() + minLeadSeconds * 1000) {
    return { ok: false, error: "Pick a time at least 1 minute from now. To send immediately, use Send now." };
  }
  if (date.getTime() > now.getTime() + maxHorizonDays * 86400000) {
    return { ok: false, error: `Notifications can be scheduled at most ${maxHorizonDays} days ahead.` };
  }
  const actual = toZonedInputValue(date, timeZone);
  const notice = actual !== value
    ? `${pad(h)}:${pad(mi)} doesn't exist on that day in ${timeZone ? "the store's" : "your"} time zone (clock change). It will be sent at ${actual.slice(11)}.`
    : null;
  return { ok: true, date, iso: date.toISOString(), notice };
}

const FORMAT = {
  weekday: "short", day: "numeric", month: "short", year: "numeric",
  hour: "numeric", minute: "2-digit", timeZoneName: "short",
};

/**
 * Format an instant in a given IANA zone ("Sat, 27 Sept 2026, 9:30 am IST").
 * Falls back to the browser's zone if the stored zone is unknown.
 */
export function formatInZone(iso, timeZone) {
  const d = new Date(iso);
  if (!iso || Number.isNaN(d.getTime())) return "";
  try {
    return new Intl.DateTimeFormat(undefined, { ...FORMAT, timeZone: timeZone || undefined }).format(d);
  } catch {
    return new Intl.DateTimeFormat(undefined, FORMAT).format(d);
  }
}

export function formatUtc(iso) {
  return formatInZone(iso, "UTC");
}

/** "Asia/Kolkata (GMT+5:30)" — a zone name with its current UTC offset. */
export function zoneLabel(timeZone, at = new Date()) {
  try {
    const off = new Intl.DateTimeFormat("en-US", { timeZone, timeZoneName: "shortOffset" })
      .formatToParts(at).find((p) => p.type === "timeZoneName")?.value;
    return off ? `${timeZone} (${off})` : timeZone;
  } catch {
    return timeZone || "";
  }
}

/**
 * Whether two zone names mean the same clock. Names alone are not enough:
 * "Asia/Calcutta" is the legacy alias of "Asia/Kolkata" and browsers report
 * either. Compares the wall clock both zones show at several instants.
 */
export function sameZone(a, b) {
  if (!a || !b) return false;
  if (a === b) return true;
  try {
    return [Date.UTC(2026, 0, 15, 12), Date.UTC(2026, 3, 15, 12), Date.UTC(2026, 6, 15, 12), Date.UTC(2026, 9, 28, 12)]
      .every((t) => toZonedInputValue(new Date(t), a) === toZonedInputValue(new Date(t), b));
  } catch {
    return false;
  }
}

/** Every IANA zone this browser knows, with a short fallback list. */
export function allTimeZones() {
  try {
    if (typeof Intl.supportedValuesOf === "function") return Intl.supportedValuesOf("timeZone");
  } catch { /* fall through */ }
  return ["Asia/Kolkata", "Asia/Dubai", "Asia/Singapore", "Europe/London", "America/New_York", "UTC"];
}

// ---------------------------------------------------------------- recurring

/** Weekday numbering matches the API: 0 = Monday … 6 = Sunday. */
export const WEEKDAYS = [
  { key: 0, short: "Mon", long: "Monday" }, { key: 1, short: "Tue", long: "Tuesday" },
  { key: 2, short: "Wed", long: "Wednesday" }, { key: 3, short: "Thu", long: "Thursday" },
  { key: 4, short: "Fri", long: "Friday" }, { key: 5, short: "Sat", long: "Saturday" },
  { key: 6, short: "Sun", long: "Sunday" },
];

/** "14:05" → "2:05 PM". */
export function formatTime12(hhmm) {
  const m = /^(\d{2}):(\d{2})$/.exec(hhmm || "");
  if (!m) return hhmm || "";
  const h = Number(m[1]);
  return `${h % 12 === 0 ? 12 : h % 12}:${m[2]} ${h < 12 ? "AM" : "PM"}`;
}

/** "Every day at 10:00 AM" / "Every Mon, Wed, Fri at 10:00 AM". */
export function describeRecurrence({ frequency, weekdays = [], time } = {}) {
  const at = `at ${formatTime12(time)}`;
  const days = [...new Set(weekdays)].sort((a, b) => a - b);
  if (frequency === "daily" || days.length === 7) return `Every day ${at}`;
  if (!days.length) return `Every week ${at}`;
  if (days.length === 1) return `Every ${WEEKDAYS[days[0]].long} ${at}`;
  return `Every ${days.map((d) => WEEKDAYS[d].short).join(", ")} ${at}`;
}

const DATE_ONLY = { day: "numeric", month: "short", year: "numeric", timeZone: "UTC" };

/** "5 Oct 2026" for a "YYYY-MM-DD" calendar date (no zone shifting). */
export function formatDateOnly(isoDate) {
  const d = new Date(`${isoDate}T00:00:00Z`);
  return isoDate && !Number.isNaN(d.getTime()) ? new Intl.DateTimeFormat(undefined, DATE_ONLY).format(d) : "";
}

/** "No end date" / "Until 31 Dec 2026" / "10 times (3 done)". Runs, not deliveries. */
export function describeEnd({ end_type: endType, until_date: until, max_occurrences: max, occurrences_created: sent } = {}) {
  if (endType === "until") return `Until ${formatDateOnly(until)}`;
  if (endType === "count") {
    return `${max} time${max === 1 ? "" : "s"}${typeof sent === "number" ? ` (${sent} done)` : ""}`;
  }
  return "No end date";
}

/**
 * Validate the recurring form before asking the server.
 * Returns { ok: true } or { ok: false, error }.
 */
export function validateRecurrence(r, { timeZone, now = new Date(), maxOccurrences = 365 } = {}) {
  if (r.frequency === "weekly" && !(r.weekdays || []).length) return { ok: false, error: "Pick at least one day of the week." };
  if (!/^([01]\d|2[0-3]):[0-5]\d$/.test(r.time || "")) return { ok: false, error: "Pick a time." };
  if (!/^\d{4}-\d{2}-\d{2}$/.test(r.start_date || "")) return { ok: false, error: "Pick a start date." };
  if (r.start_date < todayInZone(timeZone, now)) return { ok: false, error: "The start date has already passed." };
  if (r.end_type === "until") {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(r.until_date || "")) return { ok: false, error: "Pick an end date." };
    if (r.until_date < r.start_date) return { ok: false, error: "The end date is before the start date." };
  }
  if (r.end_type === "count") {
    const n = Number(r.max_occurrences);
    if (!Number.isInteger(n) || n < 1 || n > maxOccurrences) {
      return { ok: false, error: `Number of times must be between 1 and ${maxOccurrences}.` };
    }
  }
  return { ok: true };
}
